import { useToast } from '@chakra-ui/react';
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { type DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import {
  type AIQuestionGenerationResponse,
  type AiQuestionModel,
  type AiQuestionsRequest,
  type CompanyBasicDto,
  type InterviewQuestionsResponse,
  type InterviewReplicateRequest,
  type InterviewReportDto,
  type InterviewRequest,
  InterviewService,
  type InterviewTemplateDetailResponse,
  type InterviewTemplateRequest,
  InterviewTemplateService,
  type PutCandidateStatusRequest,
  type SkillRequest,
  type TemplateCreateInterviewRequest,
  type UpdateInterviewQuestionRequest,
} from '@/services/api';

export const InterviewKeys = {
  skillsQueryKey: () => ['interviewSkills'] as const,
  reportShareQueryKey: (props: ExplicitAny) => ['reportShare', props] as const,
  reportExcelQueryKey: (props: ExplicitAny) => ['reportExcel', props] as const,
  companyListQueries: () => ['companyList'] as const,
  templateQuestionListQueriesKey: () => ['TemplateQuestionList'] as const,
  templateInterview: (interviewId: string) =>
    ['templateInterview', interviewId] as const,
  templateInterviewQuestions: (interviewId: string) =>
    ['templateInterviewQuestions', interviewId] as const,
};

export const useCreateInterviewMutation = (
  options: UseMutationOptions<string, AxiosError, InterviewRequest>
) => {
  return useMutation<string, AxiosError, InterviewRequest>({
    mutationFn: async (body) => {
      return InterviewService.postInterviews({
        body,
      });
    },
    ...options,
  });
};

export const useCreateAndGetInterviewAiQuestionsMutation = (
  options?: UseMutationOptions<
    Array<AiQuestionModel>,
    AxiosError,
    AiQuestionsRequest
  >
) => {
  return useMutation<Array<AiQuestionModel>, AxiosError, AiQuestionsRequest>({
    mutationFn: async (body) => {
      const createResponse = await InterviewService.postInterviewsQuestionsAi({
        body,
      });

      // Poll the results using the requestId until isWaiting is false
      let pollResponse: AIQuestionGenerationResponse;
      do {
        pollResponse = await InterviewService.getInterviewsQuestionsAi({
          requestId: createResponse,
        });

        if (pollResponse.isWaiting) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); // wait for 1 second before polling again
        }
      } while (pollResponse.isWaiting);

      // Return the final list of AI questions
      return pollResponse.data ?? [];
    },
    ...options,
  });
};

export const useGetInterviewSkillsQuery = (
  options?: Partial<UseQueryOptions<Array<string>, AxiosError>>
) => {
  return useQuery<Array<string>, AxiosError>({
    queryKey: InterviewKeys.skillsQueryKey(),
    queryFn: () =>
      InterviewService.getInterviewSkill(options?.axiosRequestConfig),
    ...options,
  });
};

export const useCreateInterviewSkillMutation = (
  options?: UseMutationOptions<void, AxiosError, SkillRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, SkillRequest>({
    mutationFn: async (body) => {
      return InterviewService.postInterviewSkill({
        body,
      });
    },
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({
        queryKey: InterviewKeys.skillsQueryKey(),
      });

      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

type ChangeInterviewStatusRequest = {
  interviewsId: string;
  candidateId: string;
  body?: PutCandidateStatusRequest;
};

export const useChangeInterviewStatusMutation = (
  options: UseMutationOptions<string, AxiosError, ChangeInterviewStatusRequest>
) => {
  return useMutation<string, AxiosError, ChangeInterviewStatusRequest>({
    mutationFn: async (body) => {
      return InterviewService.putInterviewsCandidatesStatusById(body);
    },
    ...options,
  });
};

export const useReplicateInterviewMutation = (
  options: UseMutationOptions<string, AxiosError, InterviewReplicateRequest>
) => {
  const queryClient = useQueryClient();
  return useMutation<string, AxiosError, InterviewReplicateRequest>({
    mutationFn: async (body) => {
      return InterviewService.postInterviewsReplicate({ body });
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: ['getInterviews'],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useGetInterviewReport = (
  interviewsId: string,
  dateRange?: DateRange,
  options?: Partial<UseQueryOptions<InterviewReportDto, AxiosError>>
) => {
  return useQuery<InterviewReportDto, AxiosError>({
    queryKey: InterviewKeys.reportShareQueryKey({ interviewsId, dateRange }),
    queryFn: () =>
      InterviewService.getAllInterviewsReport(
        {
          interviewsId,
          completedAtFrom: dateRange?.from?.toISOString(),
          completedAtTo: dateRange?.to?.toISOString(),
        },
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type ShareInterviewReport = {
  interviewsId: string;
};

export const useShareInterviewReportMutation = (
  options?: UseMutationOptions<string, AxiosError, ShareInterviewReport>
) => {
  return useMutation<string, AxiosError, ShareInterviewReport>({
    mutationFn: async (body) => {
      return InterviewService.postAllInterviewsReportShare(body);
    },
    ...options,
  });
};

type ReportQuestionSummary = {
  interviewsId: string;
  questionId: string;
  email: string;
};

export const useReportQuestionSummaryMutation = (
  options?: UseMutationOptions<string, AxiosError, ReportQuestionSummary>
) => {
  return useMutation<string, AxiosError, ReportQuestionSummary>({
    mutationFn: async (body) =>
      await InterviewService.postInterviewsReportQuestionsSummaryById(body),
    ...options,
  });
};

type useGetInterviewShareReport = {
  interviewsId: string;
  reportId: string;
};

export const useGetInterviewShareReport = (
  request: useGetInterviewShareReport,
  options?: Partial<UseQueryOptions<InterviewReportDto, AxiosError>>
) => {
  return useQuery<InterviewReportDto, AxiosError>({
    queryKey: InterviewKeys.reportShareQueryKey(request.interviewsId),
    queryFn: () =>
      InterviewService.getInterviewsReportById(
        { ...request },
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type getInterviewReportExcel = {
  interviewsId: string;
  dateFrom?: Date;
  dateTo?: Date;
};

export const useExportCandidatesMutation = (
  options?: Partial<
    UseMutationOptions<string, AxiosError, getInterviewReportExcel>
  >
) => {
  return useMutation<string, AxiosError, getInterviewReportExcel>({
    mutationFn: (params) => {
      return InterviewService.getAllInterviewsReportExcel(
        {
          ...params,
          dateFrom: params?.dateFrom?.toISOString(),
          dateTo: params?.dateTo?.toISOString(),
        },
        options?.axiosRequestConfig
      );
    },
    ...options,
  });
};

export const useCreateTemplateInterviewMutation = (
  options: UseMutationOptions<string, AxiosError, InterviewTemplateRequest>
) => {
  return useMutation<string, AxiosError, InterviewTemplateRequest>({
    mutationFn: async (body) => {
      return InterviewTemplateService.postInterviewTemplate({
        body,
      });
    },
    ...options,
  });
};

export const useGetInterviewTemplateCompanyList = (
  options?: Partial<UseQueryOptions<Array<CompanyBasicDto>, AxiosError>>
) => {
  return useQuery<Array<CompanyBasicDto>, AxiosError>({
    queryKey: InterviewKeys.companyListQueries(),
    queryFn: () =>
      InterviewTemplateService.getInterviewTemplateCompanyList(
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type AssignTemplateInterviewRequest = {
  interviewTemplateId: string;
  body?: TemplateCreateInterviewRequest;
};

export const useAssignTemplateInterviewMutation = (
  options?: UseMutationOptions<
    string,
    AxiosError,
    AssignTemplateInterviewRequest
  >
) => {
  return useMutation<string, AxiosError, AssignTemplateInterviewRequest>({
    mutationFn: async (request) => {
      return InterviewTemplateService.postAllInterviewTemplateCreateInterview(
        request
      );
    },
    ...options,
  });
};

export const useGetInterviewTemplateQuestions = (
  interviewTemplateId: string,
  options?: Partial<UseQueryOptions<InterviewQuestionsResponse, AxiosError>>
) => {
  return useQuery<InterviewQuestionsResponse, AxiosError>({
    queryKey: InterviewKeys.templateInterviewQuestions(interviewTemplateId),
    queryFn: () =>
      InterviewTemplateService.getAllInterviewTemplateQuestions({
        interviewTemplateId,
      }),
    ...options,
  });
};

export const useGetInterviewTemplate = (
  interviewTemplateId: string,
  options?: Partial<
    UseQueryOptions<InterviewTemplateDetailResponse, AxiosError>
  >
) => {
  return useQuery<InterviewTemplateDetailResponse, AxiosError>({
    queryKey: InterviewKeys.templateInterview(interviewTemplateId),
    queryFn: () =>
      InterviewTemplateService.getAllInterviewTemplate({
        interviewTemplateId,
      }),
    ...options,
  });
};

type UpdateTemplateInterviewQuestionsRequest = {
  interviewTemplateId: string;
  body?: UpdateInterviewQuestionRequest;
};

export const useEditTemplateInterviewMutation = (
  options?: UseMutationOptions<
    string,
    AxiosError,
    UpdateTemplateInterviewQuestionsRequest
  >
) => {
  const { t } = useTranslation(['interview']);
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation<
    string,
    AxiosError,
    UpdateTemplateInterviewQuestionsRequest
  >({
    mutationFn: async (request) => {
      return InterviewTemplateService.putAllInterviewTemplateQuestions(request);
    },
    ...options,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({
        queryKey: InterviewKeys.templateInterviewQuestions(
          variables.interviewTemplateId
        ),
      });

      queryClient.invalidateQueries({
        queryKey: InterviewKeys.templateInterview(
          variables.interviewTemplateId
        ),
      });

      toast({
        title: t('interview:editQuestionsSuccess'),
        status: 'success',
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useDeleteTemplateInterviewMutation = (
  options?: UseMutationOptions<string, AxiosError, ExplicitAny>
) => {
  const queryClient = useQueryClient();
  return useMutation<string, AxiosError, ExplicitAny>({
    mutationFn: async (interviewTemplateId) => {
      return InterviewTemplateService.deleteInterviewTemplateById({
        interviewTemplateId,
      });
    },
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: ['getInterviewTemplates'],
      });
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};
